import { useEffect } from "react";
import { useSchema } from "../contexts/schema.context";
import { locale } from "src/config/app-config";
import {
  toAbsoluteUrl,
  getBaseUrl,
  getPathByTypeAndGender,
} from "../utils/getBaseUrl";
import { transformProductToProductData } from "src/services/schema/utils";
import { getGenderSegment } from "src/services/schema/utils/helpers";
import { generateItemList } from "src/services/schema/utils/schema-generators";
import type { Gender, ProductListType } from "src/types/product-listing";
import type { CatalogProduct } from "src/types/graphql.d";

interface ItemListSchemaProps {
  products: CatalogProduct[];
  productListType: ProductListType;
  title?: string;
  description?: string;
  slug: string;
  gender?: Gender;
  /** sameAs links for the brand (Wikidata, official site, socials) */
  brandSameAs?: string[];
  /** @id reference for the seller Corporation, e.g. "https://www.otrium.nl/#Otrium" */
  sellerIdRef?: string;
}

/**
 * Custom hook for managing ItemList schema generation on PLPs.
 * Replaces useOfferCatalogSchema with the @graph-compatible ItemList pattern
 * that marketing/SEO expects.
 */
export const useItemListSchema = ({
  products,
  productListType,
  title,
  description,
  slug,
  gender,
  brandSameAs,
  sellerIdRef,
}: ItemListSchemaProps): void => {
  const { addSchemas, removeSchemas } = useSchema();

  useEffect(() => {
    const schemaId = `item-list-${gender}-${slug}`;

    if (!products || products.length === 0) {
      removeSchemas(schemaId);
      return;
    }

    // Must match the CollectionPage @id built in usePLPSchema so that
    // mainEntityOfPage resolves within the @graph.
    const catalogPath = getPathByTypeAndGender({
      type: productListType === "BRAND_PLP" ? "brand" : "category",
      gender: getGenderSegment(gender),
      slug,
      locale,
    });
    const catalogUrl = toAbsoluteUrl(catalogPath) || "";
    const catalogName = title || `${gender} ${slug}`;
    const catalogDescription = description || `Shop ${catalogName} at Otrium`;

    const transformedProducts = products.map((product) => {
      const productData = transformProductToProductData(product, {
        baseUrl: getBaseUrl(locale),
        defaultCurrency: "EUR",
        sellerName: "Otrium",
        gender,
        locale,
      });

      // Enrich with brand sameAs and seller @id ref if provided
      if (brandSameAs) {
        productData.brandSameAs = brandSameAs;
      }
      if (sellerIdRef) {
        productData.sellerIdRef = sellerIdRef;
      }

      return productData;
    });

    // Generate ItemList schema
    const itemListSchema = generateItemList(
      catalogName,
      catalogUrl,
      transformedProducts,
      catalogDescription
    );

    addSchemas([itemListSchema], schemaId);

    // Cleanup on unmount
    return () => removeSchemas(schemaId);
  }, [
    products,
    productListType,
    title,
    description,
    slug,
    gender,
    brandSameAs,
    sellerIdRef,
    locale,
    addSchemas,
    removeSchemas,
  ]);
};
